
import * as THREE from '/assets/scripts/three.module.js';
import { socketLandmarkLocal } from './motherboard-landmarks.js?v=20260817-pcieanchor10';

function finite(value, fallback=0){const n=Number(value);return Number.isFinite(n)?n:fallback;}

function disposeObject(root){
  root?.traverse?.(node=>{
    node.geometry?.dispose?.();
    if(node.material){
      const mats=Array.isArray(node.material)?node.material:[node.material];
      mats.forEach(mat=>mat?.dispose?.());
    }
  });
}

function blockMaterial(){
  return new THREE.MeshStandardMaterial({color:0x111318,roughness:0.62,metalness:0.08});
}
function tubeMaterial(){
  return new THREE.MeshStandardMaterial({color:0x171a1f,roughness:0.92,metalness:0.01});
}

function buildCpuBlock(radiator,motherboard){
  const meta=radiator?.componentMeta?.aioBlock;
  if(!meta||finite(meta.h,0)<=0)return null;
  const l=finite(meta.l,70),w=finite(meta.w,70),h=finite(meta.h,0);
  if(h<=0)return null;
  const group=new THREE.Group();
  group.name=`aio_cpu_block_${radiator.uid}`;
  // Mark as a nested component boundary so generic motherboard bounding-box traversal does
  // not accidentally promote this advisory CPU-side assembly into motherboard dimensions.
  group.isComponent=true;
  group.trackType='aio_block_aux';
  group.userData={sffAioAuxiliary:true,radiatorUid:radiator.uid,dimensionKnown:{...(meta.dimensionKnown||{})},dimensionSource:meta.dimensionSource||'unknown'};
  const mesh=new THREE.Mesh(new THREE.BoxGeometry(w,h,l),blockMaterial());
  mesh.position.y=h/2;
  mesh.castShadow=true;mesh.receiveShadow=true;
  group.add(mesh);
  const edges=new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry),new THREE.LineBasicMaterial({color:0x9ca3af,transparent:true,opacity:0.72}));
  edges.position.copy(mesh.position);edges.userData.sffAioAuxiliary=true;group.add(edges);
  const socket=socketLandmarkLocal(motherboard.nativeSpecs||{},motherboard.componentMeta?.formFactor||'ITX');
  group.position.set(socket[0],socket[1],socket[2]);
  motherboard.add(group);
  group.updateMatrixWorld(true);
  return group;
}

function blockPorts(block,radiatorMeta){
  const h=finite(radiatorMeta?.aioBlock?.h,40),w=finite(radiatorMeta?.aioBlock?.w,70),l=finite(radiatorMeta?.aioBlock?.l,70);
  const localY=Math.max(8,h*0.72);
  const localZ=Math.max(10,l*0.28);
  const spread=Math.min(18,Math.max(10,w*0.20));
  return [
    new THREE.Vector3(-spread,localY,localZ).applyMatrix4(block.matrixWorld),
    new THREE.Vector3(spread,localY,localZ).applyMatrix4(block.matrixWorld)
  ];
}

function worldDirection(object,localVector){
  const q=object.getWorldQuaternion(new THREE.Quaternion());
  return localVector.clone().applyQuaternion(q).normalize();
}

function radiatorPorts(radiator,blockStarts=[]){
  const dims=radiator.nativeSpecs||{};
  const w=finite(dims.w,120),l=finite(dims.l,277);
  const spread=Math.min(20,Math.max(10,w*0.18));
  const endOffset=Math.max(8,l/2-8);
  const avgStart=blockStarts.length
    ? blockStarts.reduce((acc,p)=>acc.add(p),new THREE.Vector3()).multiplyScalar(1/blockStarts.length)
    : radiator.getWorldPosition(new THREE.Vector3());

  const makeEnd=sign=>{
    const a=new THREE.Vector3(-spread,0,sign*endOffset).applyMatrix4(radiator.matrixWorld);
    const b=new THREE.Vector3(spread,0,sign*endOffset).applyMatrix4(radiator.matrixWorld);
    const center=a.clone().add(b).multiplyScalar(0.5);
    return {ports:[a,b],center,sign};
  };
  // Connect to whichever radiator tank/end is physically closer to the CPU block instead of
  // always assuming local +Z. This keeps tubes attached to the sensible end after the radiator
  // is rotated/flipped by the solver.
  const positive=makeEnd(1),negative=makeEnd(-1);
  const chosen=positive.center.distanceTo(avgStart)<=negative.center.distanceTo(avgStart)?positive:negative;
  const exitDirection=worldDirection(radiator,new THREE.Vector3(0,0,chosen.sign));
  return {ports:chosen.ports,exitDirection};
}

function blockExitDirection(block){
  return worldDirection(block,new THREE.Vector3(0,1,0));
}

function routeCurve(start,end,startExit,endExit,offsetSign=1,tubeBudget=350){
  const delta=end.clone().sub(start);
  const direct=Math.max(1,delta.length());
  const budget=Math.max(direct,finite(tubeBudget,350));
  const slack=Math.max(0,budget-direct);
  const tangentReach=Math.min(95,Math.max(24,direct*0.20 + slack*0.12));
  const bow=Math.min(110,Math.max(12,direct*0.06 + slack*0.32))*offsetSign;

  // Build a lateral vector that is perpendicular to the direct route where possible. The
  // cubic Bezier guarantees exact endpoints and endpoint tangents, avoiding Catmull-Rom
  // overshoot that previously made tubes appear to leave/re-enter the radiator body.
  const directDir=delta.clone().normalize();
  let lateral=new THREE.Vector3().crossVectors(directDir,startExit).normalize();
  if(lateral.lengthSq()<1e-6)lateral=new THREE.Vector3(1,0,0);
  lateral.multiplyScalar(bow);

  const c1=start.clone().addScaledVector(startExit,tangentReach).add(lateral);
  const c2=end.clone().addScaledVector(endExit,tangentReach).addScaledVector(lateral,0.55);
  return new THREE.CubicBezierCurve3(start.clone(),c1,c2,end.clone());
}

function endpointHash(points){
  return points.map(p=>`${p.x.toFixed(2)},${p.y.toFixed(2)},${p.z.toFixed(2)}`).join('|');
}

function buildTubeMesh(curve){
  const geometry=new THREE.TubeGeometry(curve,18,4.5,6,false);
  const mesh=new THREE.Mesh(geometry,tubeMaterial());
  mesh.castShadow=true;mesh.receiveShadow=true;
  mesh.userData.sffAioAuxiliary=true;
  return mesh;
}

function worldBoxFor(root){
  root.updateMatrixWorld(true);
  return new THREE.Box3().setFromObject(root);
}

function advisoryState(block,radiator,motherboard,activeBoxes,tubeLengths,caseBox=null){
  const advisories=[];
  if(block){
    const blockBox=worldBoxFor(block).clone().expandByScalar(-0.3);
    const shell=caseBox?.children?.find?.(child=>child?.isMesh&&child?.isCase) || null;
    if(shell){
      const caseBounds=new THREE.Box3().setFromObject(shell).clone().expandByScalar(0.5);
      if(!caseBounds.containsBox(blockBox))advisories.push({type:'aio_block_case_clearance',message:'AIO CPU block reaches outside the simplified case envelope.'});
    }
    for(const item of activeBoxes||[]){
      if(!item?.isComponent||item===radiator||item===motherboard||item.trackType==='fan')continue;
      const box=new THREE.Box3().setFromObject(item).clone().expandByScalar(-0.3);
      if(blockBox.intersectsBox(box))advisories.push({type:'aio_block_clearance',message:`AIO block overlaps ${item.displayLabel||item.trackType||'hardware'} in the simplified model.`});
    }
  }
  const availableTube=finite(radiator?.componentMeta?.tubeLength,350)||350;
  if(tubeLengths.length){
    const maxRoute=Math.max(...tubeLengths);
    // Curves are deliberately loose visual routes; only flag a meaningful exceedance. Unknown
    // source length uses the explicit 350 mm visual/advisory fallback and says so.
    if(maxRoute>availableTube+12){
      const assumed=!!radiator?.componentMeta?.tubeLengthAssumed || !radiator?.componentMeta?.tubeLengthKnown;
      advisories.push({type:'aio_tube_reach',message:assumed
        ? `Primitive tube route is ~${Math.round(maxRoute)} mm vs the 350 mm fallback tube estimate; verify the actual AIO tube length.`
        : `Primitive tube route is ~${Math.round(maxRoute)} mm vs ${Math.round(availableTube)} mm catalog tube length.`});
    }
  }
  return advisories;
}

export function createAioAssemblyManager({scene,activeBoxes,getCaseBox,onAdvisoryChange=()=>{},markRenderDirty=()=>{}}={}){
  const assemblies=new Map();

  function remove(radiatorUid){
    const entry=assemblies.get(radiatorUid);if(!entry)return;
    if(entry.radiator){
      entry.radiator.aioAssemblyAdvisories=[];
      onAdvisoryChange(entry.radiator);
    }
    entry.block?.parent?.remove(entry.block);disposeObject(entry.block);
    entry.tubeGroup?.parent?.remove(entry.tubeGroup);disposeObject(entry.tubeGroup);
    assemblies.delete(radiatorUid);
    markRenderDirty();
  }

  function refreshEntry(entry){
    const {radiator,motherboard,block,tubeGroup}=entry;
    if(!radiator?.parent||!motherboard?.parent||!block?.parent){remove(radiator?.uid);return;}
    block.updateMatrixWorld(true);radiator.updateMatrixWorld(true);
    const starts=blockPorts(block,radiator.componentMeta);
    const radiatorEnd=radiatorPorts(radiator,starts);
    const ends=radiatorEnd.ports;
    const points=[starts[0],starts[1],ends[0],ends[1]];
    const hash=endpointHash(points);
    if(hash===entry.lastHash)return;
    entry.lastHash=hash;
    const startExit=blockExitDirection(block);
    const tubeBudget=finite(radiator?.componentMeta?.tubeLength,350)||350;
    const routes=[
      routeCurve(starts[0],ends[0],startExit,radiatorEnd.exitDirection,-1,tubeBudget),
      routeCurve(starts[1],ends[1],startExit,radiatorEnd.exitDirection,1,tubeBudget)
    ];
    entry.tubes.forEach((tube,index)=>{
      tube.geometry?.dispose?.();
      tube.geometry=new THREE.TubeGeometry(routes[index],18,4.5,6,false);
    });
    const lengths=routes.map(curve=>curve.getLength());
    const advisories=advisoryState(block,radiator,motherboard,activeBoxes,lengths,getCaseBox?.()||null);
    const advisoryHash=JSON.stringify(advisories);
    if(advisoryHash!==entry.advisoryHash){
      entry.advisoryHash=advisoryHash;
      radiator.aioAssemblyAdvisories=advisories;
      onAdvisoryChange(radiator);
    }
  }

  function ensure(){
    const radiators=(activeBoxes||[]).filter(item=>item?.isComponent&&item.trackType==='radiator');
    const motherboards=(activeBoxes||[]).filter(item=>item?.isComponent&&item.trackType==='motherboard');
    // Simulator mode naturally has one primary slot. In free Workbench mode there may be
    // several boards/AIOs, so do not invent an arbitrary pairing when the relationship is
    // ambiguous. A single board + single AIO is safe to visualize as one assembly.
    if(radiators.length!==1 || motherboards.length!==1){
      clear();
      return null;
    }
    const radiator=radiators[0];
    const motherboard=motherboards[0];
    for(const uid of [...assemblies.keys()])if(uid!==radiator.uid)remove(uid);
    if(!radiator.componentMeta?.aioBlock||finite(radiator.componentMeta.aioBlock.h,0)<=0)return null;
    let entry=assemblies.get(radiator.uid);
    if(entry&&entry.motherboard!==motherboard){remove(radiator.uid);entry=null;}
    if(!entry){
      const block=buildCpuBlock(radiator,motherboard);if(!block)return null;
      const starts=blockPorts(block,radiator.componentMeta);radiator.updateMatrixWorld(true);const radiatorEnd=radiatorPorts(radiator,starts);const ends=radiatorEnd.ports;
      const tubeGroup=new THREE.Group();tubeGroup.name=`aio_tubes_${radiator.uid}`;tubeGroup.userData.sffAioAuxiliary=true;
      const startExit=blockExitDirection(block);
      const tubeBudget=finite(radiator?.componentMeta?.tubeLength,350)||350;
      const routes=[
        routeCurve(starts[0],ends[0],startExit,radiatorEnd.exitDirection,-1,tubeBudget),
        routeCurve(starts[1],ends[1],startExit,radiatorEnd.exitDirection,1,tubeBudget)
      ];
      const tubes=routes.map(buildTubeMesh);tubes.forEach(tube=>tubeGroup.add(tube));scene.add(tubeGroup);
      entry={radiator,motherboard,block,tubeGroup,tubes,lastHash:'',advisoryHash:''};
      assemblies.set(radiator.uid,entry);
      const refresh=()=>refreshEntry(entry);
      tubes.forEach(tube=>{tube.onBeforeRender=refresh;});
      refreshEntry(entry);
      markRenderDirty();
    }
    return entry;
  }

  function removeForObject(object){
    if(!object)return;
    if(object.trackType==='radiator')remove(object.uid);
    else if(object.trackType==='motherboard')for(const uid of [...assemblies.keys()])remove(uid);
  }

  function clear(){for(const uid of [...assemblies.keys()])remove(uid);}
  return {ensure,remove,removeForObject,clear,get:(uid)=>assemblies.get(uid)||null,get size(){return assemblies.size;}};
}
